import { Box, Chip, Link, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useNavigate } from "react-router"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"

/**
 * The tenant, as CONTEXT (LOCKER-421 `DoD8`, `OOS10`).
 *
 * A tenant selects lockers and groups their runs. It is never itself assessed, and
 * this row is deliberately the weakest thing on the page: a chip, a name, an id, a
 * count of sibling lockers, and a link back to the list filtered to it.
 *
 * **It carries no verdict and no drift total, and it must not grow one.** Nothing
 * sums across lockers — an inconclusive run beside a clean one cannot be added —
 * so any tenant figure would be a guess wearing the same typography as a measured
 * one. There is also no `/sa/reservation-syncs/tenant/:tenantId` route: a tenant
 * has nothing of its own to show.
 */
export const TenantContextRow: FC<{
  run: ReservationSyncDto
  /** Other lockers in this tenant that hold runs. A count of ROWS, not a verdict. */
  siblingLockerCount: number
}> = ({ run, siblingLockerCount }) => {
  const navigate = useNavigate()
  const toList = () =>
    navigate(
      `/sa/reservation-syncs?tenantId=${encodeURIComponent(run.tenantId)}`,
    )

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      flexWrap="wrap"
      sx={{ pb: 1.5, mb: 2, borderBottom: 1, borderColor: "divider" }}
    >
      <Chip
        size="small"
        label="TENANT"
        sx={{ fontWeight: 700, letterSpacing: "0.07em", height: 20 }}
      />
      <Link
        component="button"
        type="button"
        onClick={toList}
        sx={{ fontSize: "0.82rem", fontWeight: 600, textAlign: "left" }}
      >
        {run.tenantName ?? run.tenantId}
      </Link>
      <Typography
        variant="caption"
        sx={{ fontFamily: "monospace", color: "text.disabled" }}
      >
        {run.tenantId}
      </Typography>
      <Box sx={{ color: "divider" }}>|</Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {siblingLockerCount === 1
          ? "1 locker holds reservation runs"
          : `${siblingLockerCount} lockers hold reservation runs`}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Link
        component="button"
        type="button"
        onClick={toList}
        sx={{ fontSize: "0.72rem", fontWeight: 600 }}
      >
        All runs in this tenant →
      </Link>
    </Stack>
  )
}
